export const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL?.replace(/\/$/, "") || "https://api.dotehermes.com";

export interface User {
  id: string;
  email: string;
  display_name: string | null;
  is_admin: boolean;
}

export interface TokenPair {
  access_token: string;
  refresh_token: string;
  token_type: "bearer";
  expires_in: number;
  user: User;
}

export interface AppInfo {
  id: string;
  name: string;
  description: string;
  kb_collections: string[];
}

export interface ChatSession {
  id: string;
  app_id: string;
  title: string;
  car_slug: string | null;
  acp_session_id: string | null;
  created_at: string;
  last_message_at: string | null;
}

export interface CarProject {
  car_slug: string | null;
  car_name: string;
  session_count: number;
  last_message_at: string | null;
}

export interface KnownCar {
  slug: string;
  name: string;
  make: string | null;
  model: string | null;
  year: number | null;
}

export interface PersistedMessage {
  id: string;
  role: "user" | "assistant" | string;
  content_json: string;
  created_at: string;
}

export interface InviteInfo {
  code: string;
  email: string | null;
  state: "open" | "expired" | "redeemed" | string;
}

export interface InviteCreateResult extends InviteInfo {
  invite_url: string;
  created_at: string;
  expires_at: string | null;
  redeemed_at: string | null;
  redeemed_by_user_id: string | null;
  email_sent: boolean | null;
  email_error: string | null;
}

export interface ApiClientOptions {
  getAccessToken: () => string | null;
  refreshTokens: () => Promise<string | null>;
  onUnauthorized: () => void;
}

export class ApiClient {
  private readonly options: ApiClientOptions;

  constructor(options: ApiClientOptions) {
    this.options = options;
  }

  login(email: string, password: string): Promise<TokenPair> {
    return this.request<TokenPair>("/v1/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
      auth: false,
    });
  }

  refresh(refreshToken: string): Promise<TokenPair> {
    return this.request<TokenPair>("/v1/auth/refresh", {
      method: "POST",
      body: JSON.stringify({ refresh_token: refreshToken }),
      auth: false,
    });
  }

  getInvite(code: string): Promise<InviteInfo> {
    return this.request<InviteInfo>(`/v1/auth/invites/${encodeURIComponent(code)}`, {
      auth: false,
    });
  }

  redeemInvite(input: {
    code: string;
    email: string;
    password: string;
    displayName?: string;
  }): Promise<TokenPair> {
    return this.request<TokenPair>("/v1/auth/redeem", {
      method: "POST",
      body: JSON.stringify({
        code: input.code,
        email: input.email,
        password: input.password,
        display_name: input.displayName || null,
      }),
      auth: false,
    });
  }

  createInvite(input: {
    email: string;
    expiresInDays?: number;
    sendEmail?: boolean;
  }): Promise<InviteCreateResult> {
    return this.request<InviteCreateResult>("/admin/invites", {
      method: "POST",
      body: JSON.stringify({
        email: input.email,
        expires_in_days: input.expiresInDays ?? 30,
        send_email: input.sendEmail ?? true,
      }),
    });
  }

  listApps(): Promise<AppInfo[]> {
    return this.request<AppInfo[]>("/v1/apps", { auth: false });
  }

  listSessions(appId = "dote"): Promise<ChatSession[]> {
    return this.request<ChatSession[]>(`/v1/apps/${appId}/sessions`);
  }

  createSession(
    appId = "dote",
    input: { title?: string; carSlug?: string | null } = {},
  ): Promise<ChatSession> {
    return this.request<ChatSession>(`/v1/apps/${appId}/sessions`, {
      method: "POST",
      body: JSON.stringify({
        title: input.title,
        car_slug: input.carSlug ?? null,
      }),
    });
  }

  listProjects(appId = "dote"): Promise<CarProject[]> {
    return this.request<CarProject[]>(`/v1/apps/${appId}/projects`);
  }

  listKnownCars(): Promise<KnownCar[]> {
    return this.request<KnownCar[]>("/v1/apps/dote/cars/known");
  }

  deleteSession(appId: string, sessionId: string): Promise<void> {
    return this.request<void>(`/v1/apps/${appId}/sessions/${sessionId}`, {
      method: "DELETE",
    });
  }

  listMessages(appId: string, sessionId: string): Promise<PersistedMessage[]> {
    return this.request<PersistedMessage[]>(
      `/v1/apps/${appId}/sessions/${sessionId}/messages`,
    );
  }

  async authorizedFetch(path: string, init: RequestInit = {}): Promise<Response> {
    const first = await this.rawFetch(path, init);
    if (first.status !== 401) {
      return first;
    }

    const refreshedToken = await this.options.refreshTokens();
    if (!refreshedToken) {
      this.options.onUnauthorized();
      return first;
    }

    return this.rawFetch(path, init, refreshedToken);
  }

  private async request<T>(
    path: string,
    init: RequestInit & { auth?: boolean } = {},
  ): Promise<T> {
    const { auth = true, ...requestInit } = init;
    const response = auth
      ? await this.authorizedFetch(path, requestInit)
      : await fetch(`${API_BASE_URL}${path}`, this.buildInit(requestInit));

    if (!response.ok) {
      throw new ApiError(response.status, await readApiError(response));
    }

    if (response.status === 204) {
      return undefined as T;
    }

    return (await response.json()) as T;
  }

  private rawFetch(path: string, init: RequestInit = {}, overrideToken?: string): Promise<Response> {
    const token = overrideToken || this.options.getAccessToken();
    return fetch(
      `${API_BASE_URL}${path}`,
      this.buildInit(init, token ? { Authorization: `Bearer ${token}` } : undefined),
    );
  }

  private buildInit(init: RequestInit, headers?: HeadersInit): RequestInit {
    return {
      ...init,
      headers: {
        "Content-Type": "application/json",
        ...headers,
        ...init.headers,
      },
    };
  }
}

export class ApiError extends Error {
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }
}

async function readApiError(response: Response): Promise<string> {
  try {
    const body = (await response.json()) as unknown;
    if (typeof body === "object" && body && "detail" in body) {
      const detail = (body as { detail: unknown }).detail;
      return typeof detail === "string" ? detail : JSON.stringify(detail);
    }
    return JSON.stringify(body);
  } catch {
    return response.statusText || "request_failed";
  }
}
